import { useEffect } from 'react';
import { useIsLowestItemTypeLevel } from '@/common/hooks/useIsLowestItemTypeLevel';
import { toNullableNumber } from '@/common/utils/number';
import { ItemFinancialPanelView } from '@/features/financials/components/ItemFinancialPanelView';
import type { FinancialSaveState } from '@/features/financials/components/types';
import { useSetItemFinancialsMutation } from '@/features/financials/mutations/useSetItemFinancialsMutation';
import { useItemFinancialsQuery } from '@/features/financials/queries/item-financials/useItemFinancialsQuery';
import { useItemFinancialsAggregateQuery } from '@/features/financials/queries/item-financials-aggregate/useItemFinancialsAggregateQuery';
import { useOrganizationCurrencyQuery } from '@/features/settings/organization-currency/queries/useOrganizationCurrencyQuery';
import type { FinancialsTabAdapter } from '../adapters/types';
import { useTabAdapter } from '../context/TabAdapterContext';

const getSaveState = (mutation: {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
}): FinancialSaveState => {
  if (mutation.isPending) return { status: 'pending' };
  if (mutation.isError) return { status: 'error', message: mutation.error?.message ?? '' };
  if (mutation.isSuccess) return { status: 'success' };
  return { status: 'idle' };
};

const SUCCESS_INDICATOR_DISPLAY_MS = 2000;

// react-query only clears isSuccess on the next mutate() call, not on a timer.
const useFinancialFieldMutation = () => {
  const mutation = useSetItemFinancialsMutation();

  useEffect(() => {
    if (!mutation.isSuccess) return;
    const timeout = setTimeout(() => mutation.reset(), SUCCESS_INDICATOR_DISPLAY_MS);
    return () => clearTimeout(timeout);
  }, [mutation.isSuccess, mutation.reset]);

  return { mutate: mutation.mutate, saveState: getSaveState(mutation) };
};

export const Financials = () => {
  const { itemId, itemTypeId, isReadOnly } = useTabAdapter<FinancialsTabAdapter>();
  const isLowestLevel = useIsLowestItemTypeLevel(itemTypeId);
  const { data, isLoading } = useItemFinancialsQuery([itemId]);
  const financials = data?.[0];
  const {
    data: aggregates,
    isLoading: isAggregateLoading,
    isPlaceholderData: isAggregateStale,
  } = useItemFinancialsAggregateQuery([itemId]);
  const aggregate = aggregates?.[0];
  const isHierarchySourceKnown = !isAggregateLoading && !isAggregateStale;
  const { data: organizationCurrency } = useOrganizationCurrencyQuery();
  const budget = useFinancialFieldMutation();
  const capex = useFinancialFieldMutation();
  const revenue = useFinancialFieldMutation();

  const selfBudget = toNullableNumber(financials?.budgetAmount);

  return (
    <ItemFinancialPanelView
      amount={selfBudget}
      currencyCode={organizationCurrency?.currencyCode ?? null}
      isLoading={isLoading}
      isReadOnly={isReadOnly}
      isLowestLevel={isLowestLevel}
      saveState={budget.saveState}
      onSave={(amount) =>
        budget.mutate({ itemId, input: { budgetAmount: amount?.toFixed(2) ?? null } })
      }
      figures={{
        descendantsBudgetAmount: toNullableNumber(aggregate?.descendantsBudgetAmount),
        capexPercent: toNullableNumber(aggregate?.effectiveCapexPercentage),
        forecastedRevenue: toNullableNumber(aggregate?.effectiveForecastRevenue),
        hierarchyRootSource: isHierarchySourceKnown
          ? (aggregate?.hierarchyRootSource ?? null)
          : null,
        selfActualCost: toNullableNumber(aggregate?.selfActualCost),
        selfActualCostStatus: aggregate?.selfActualCostStatus ?? null,
        totalActualCost: toNullableNumber(aggregate?.rolledActualCost),
        totalActualCostStatus: aggregate?.rolledActualCostStatus ?? null,
      }}
      onCapexPercentChange={(value) =>
        capex.mutate({ itemId, input: { capexPercentage: value ?? null } })
      }
      capexSaveState={capex.saveState}
      onForecastedRevenueChange={(value) =>
        revenue.mutate({
          itemId,
          input: { forecastRevenueAmount: value?.toFixed(2) ?? null },
        })
      }
      revenueSaveState={revenue.saveState}
    />
  );
};
